/**
 * AIVideoProviderPanel — thin wrapper around ProviderPicker.
 *
 * All availability logic (ComfyUI, GPU info, routing_mode, coming-soon) is
 * computed at the option-mapping layer, producing disabled/disabledReason on
 * each PickerOption before handing the list to ProviderPicker.
 *
 * The T2V / I2V grouping is preserved via groupBy={(o) => o.meta?.kind}.
 * Install-navigation (INSTALL / START badges) uses renderCardExtras.
 */

import { memo, useMemo, useCallback } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { isServerlessMode } from '@/utils/apiMode';
import { getEffectivePricing, isLocalPricingActive } from '@/utils/localPricing';
import { useComponentStatus } from '@/hooks/useComponentStatus';
import { gateLocalComponent } from '@/utils/localComponentGate';
import { useSystemGpuInfo } from '@/hooks/useSystemGpuInfo';
import { useVideoProviderAvailability } from '@/hooks/useVideoProviderAvailability';
import {
  VIDEO_SOURCE_OPTIONS,
  VIDEO_SOURCE_INFO,
  VIDEO_SOURCES,
  type VideoSource,
  type VideoSourceOption,
} from '@/constants/videoSources';
import { ProviderPicker } from '../../common/ProviderPicker';
import type { PickerOption } from '../../common/ProviderPicker';

/** Which provider groups to show */
export type ProviderGroup = 'cloud_t2v' | 'free_local' | 'cloud_i2v';

interface AIVideoProviderPanelProps {
  value: VideoSource;
  onChange: (source: VideoSource) => void;
  /** Filter which provider groups to show. Defaults to all AI groups. */
  showGroups?: ProviderGroup[];
  disabled?: boolean;
}

/**
 * Filter VIDEO_SOURCE_OPTIONS to only include AI providers usable by the
 * standard pipeline. Excludes:
 * - stock/youtube/ai_director: not AI provider cards
 * - category 'special': talking-head / motion-transfer providers have dedicated pages
 */
function isAIProvider(option: VideoSourceOption): boolean {
  if (option.value === 'stock' || option.value === 'youtube' || option.value === 'ai_director') {
    return false;
  }
  return option.category !== 'special';
}

type VideoProviderMeta = {
  kind: 'Text-to-Video' | 'Image-to-Video';
  isLocal: boolean;
  installComponentId?: string;
};

export const AIVideoProviderPanel = memo(function AIVideoProviderPanel({
  value,
  onChange,
  showGroups,
  disabled = false,
}: AIVideoProviderPanelProps) {
  const { t } = useTranslation();
  const comfyuiStatus = useComponentStatus('comfyui');
  const { gpuInfo } = useSystemGpuInfo();
  const { availability } = useVideoProviderAvailability();

  // Installed is enough — ComfyUI auto-starts on demand + is VRAM-managed by
  // the backend (see gateLocalComponent / ComponentLifecycle), exactly like
  // Fooocus. Requiring it to be already running hid installed-but-idle providers.
  const comfyuiInstalled = comfyuiStatus?.is_installed === true;
  const isMps = gpuInfo?.backend === 'mps';
  const isCloud = isServerlessMode();

  const options = useMemo<PickerOption<VideoProviderMeta>[]>(() => {
    const aiProviders = VIDEO_SOURCE_OPTIONS.filter(isAIProvider);
    const filtered = !showGroups
      ? aiProviders
      : aiProviders.filter((o) => showGroups.includes(o.category as ProviderGroup));

    return (
      filtered
        .map((option) => {
          const sourceInfo = VIDEO_SOURCE_INFO[option.value];
          const providerAvailability = availability?.get(option.value) ?? null;

          // Availability flags
          // 2026-05-25: backend claims coming_soon for ai_kling_o1_r2v but provider is live on Image tab — override.
          const isComingSoon: boolean =
            option.value === VIDEO_SOURCES.AI_KLING_O1_R2V
              ? false
              : (providerAvailability?.coming_soon ?? option.comingSoon ?? false);
          // Installed is enough for the LOCAL badge + free pricing — the
          // component auto-starts on demand, so "not running yet" is not a
          // user-visible blocking state (gateLocalComponent, the shared rule).
          const isLocalInstalled: boolean =
            providerAvailability !== null &&
            providerAvailability.is_local &&
            providerAvailability.is_installed;
          const isLocalProvider = sourceInfo?.isLocal === true;
          const routingMode = providerAvailability?.routing_mode ?? null;
          const hasCloudFallback = routingMode === 'hybrid' || routingMode === 'cloud_only';
          const isUnavailableInCloud =
            isCloud && isLocalProvider && !hasCloudFallback && !isComingSoon;
          const isLocalNotInstalled: boolean =
            providerAvailability !== null &&
            providerAvailability.is_local &&
            !providerAvailability.is_installed &&
            !isComingSoon &&
            !(isCloud && hasCloudFallback);
          const needsApiKey: boolean =
            providerAvailability !== null &&
            !providerAvailability.is_local &&
            providerAvailability.unavailable_reason === 'missing_api_key';

          // ComfyUI AnimateDiff legacy fallback
          const isComfyuiAnimatediff = option.value === VIDEO_SOURCES.AI_ANIMATEDIFF;
          const mpsDenied = isComfyuiAnimatediff && isMps;
          const legacyComfyuiUnavailable =
            providerAvailability === null && isComfyuiAnimatediff && !comfyuiInstalled;

          const isUnavailable =
            isComingSoon || isUnavailableInCloud || legacyComfyuiUnavailable || mpsDenied;

          // Badge text — factual state indicators only.
          // START and INSTALL removed: they were cryptic to users. Actionable hints
          // are communicated via disabledReason tooltip instead.
          let badgeText: string | undefined;
          let badgeTone: 'local' | 'neutral' = 'neutral';
          if (isComingSoon) {
            badgeText = t('videoSource.badge.comingSoon', { defaultValue: 'COMING SOON' });
          } else if (providerAvailability !== null) {
            if (isLocalInstalled) {
              badgeText = t('videoSource.badge.local', { defaultValue: 'LOCAL' });
              badgeTone = 'local';
            } else if (needsApiKey) {
              badgeText = t('videoSource.badge.needsKey', { defaultValue: 'API KEY REQUIRED' });
            }
            // isLocalNotInstalled intentionally has no badge: the disabled
            // state + disabledReason tooltip carries the message.
          }

          // Tooltip / disabledReason
          let disabledReason: string | undefined;
          if (isComingSoon) {
            disabledReason = t('videoSource.tooltip.comingSoon', {
              defaultValue: 'This provider is planned but not yet available.',
            });
          } else if (isLocalNotInstalled && providerAvailability?.install_component_id) {
            disabledReason = t('videoSource.tooltip.install', {
              defaultValue: 'Click to install in Settings - Installations.',
            });
          } else if (needsApiKey) {
            disabledReason = t('videoSource.tooltip.needsKey', {
              defaultValue: 'This provider requires an API key. Configure it in settings.',
            });
          } else if (mpsDenied) {
            disabledReason = t('videoSource.errors.mpsNotSupportedAnimateDiff', {
              defaultValue:
                'AnimateDiff video is experimental on macOS — use cloud Veo/Kling for best results.',
            });
          } else if (
            isComfyuiAnimatediff &&
            comfyuiStatus !== null &&
            !comfyuiStatus.is_installed
          ) {
            disabledReason = t('videoSource.errors.comfyuiNotInstalled', {
              defaultValue: 'ComfyUI is not installed. Install it in Component Setup.',
            });
          }

          // Pricing/badge must gate on the REAL install state, not just the
          // static capability flag: a hybrid provider (e.g. ai_framepack) that
          // is NOT installed locally but has a configured cloud fallback still
          // routes to RunPod and IS billed — showing "Free" (and
          // pricingNumeric: 0, which the default price-ascending sort floats
          // to the top of the list) for it silently overcharges the user who
          // trusted the card. `isLocalInstalled` (above) already computes the
          // same is_local && is_installed fact correctly for the LOCAL badge;
          // reuse the underlying is_installed signal here for price too.
          const installed = providerAvailability?.is_installed ?? false;
          const effectivePricing = getEffectivePricing(option.pricing, isLocalProvider, {
            installed,
          });
          const localPriceActive = isLocalPricingActive(isLocalProvider, installed);

          const kind: VideoProviderMeta['kind'] =
            option.category === 'cloud_i2v' || option.requiresImage
              ? 'Image-to-Video'
              : 'Text-to-Video';

          return {
            value: option.value,
            label: option.label,
            description: isUnavailableInCloud
              ? t('videoSource.desktopOnly', {
                  defaultValue: 'Download the desktop app for free access!',
                })
              : option.description,
            pricing: localPriceActive
              ? t('common.free', { defaultValue: 'Free' })
              : effectivePricing || undefined,
            pricingNumeric: localPriceActive ? 0 : undefined,
            quality: option.quality,
            recommended: option.recommended === true,
            disabled: isUnavailable,
            disabledReason,
            badge: badgeText ? { text: badgeText, tone: badgeTone } : undefined,
            meta: {
              kind,
              isLocal: isLocalProvider,
              installComponentId: providerAvailability?.install_component_id ?? undefined,
            },
          } satisfies PickerOption<VideoProviderMeta>;
        })
        // Drop providers that have no usable path for this user.
        // A local-only provider that is not installed AND has no cloud fallback is
        // completely unreachable — showing it as a permanently-disabled card only
        // creates cognitive clutter (e.g. FramePack when not installed locally and
        // no cloud route exists).
        .filter((opt) => {
          const meta = opt.meta;
          if (!meta) return true;
          if (!meta.isLocal) return true; // pure-cloud provider — always reachable
          const avail = availability?.get(opt.value);
          // Installed (running OR idle) → reachable: the backend auto-starts +
          // VRAM-manages the component on first use (gateLocalComponent, the
          // shared rule used by the image selector + Fooocus). A cloud fallback
          // also makes it reachable. Unknown availability is shown conservatively
          // (gateLocalComponent(undefined) → selectable).
          const hasCloudFallback =
            avail?.routing_mode === 'hybrid' || avail?.routing_mode === 'cloud_only';
          return gateLocalComponent(avail).selectable || hasCloudFallback;
        })
    );
  }, [availability, comfyuiInstalled, comfyuiStatus, isMps, isCloud, showGroups, t]);

  // Install-navigation: clicking an INSTALL/START card navigates instead of selecting.
  // We intercept onChange at the wrapper level to check if the option triggers install.
  const handleChange = useCallback(
    (selectedValue: string) => {
      const opt = options.find((o) => o.value === selectedValue);
      if (opt?.meta?.installComponentId && opt.badge?.text === 'INSTALL') {
        // This app has no URL router: navigation is App.tsx's `mode` state,
        // driven by the 'navigate-to-mode' event. Assigning window.location
        // .href = '/settings/installations?...' pointed at a path nothing
        // handles, so clicking INSTALL just reloaded the app onto its default
        // page — the one thing the button must not do.
        window.dispatchEvent(
          new CustomEvent('navigate-to-mode', {
            detail: { mode: 'settings', subTab: 'installations' },
          })
        );
        return;
      }
      onChange(selectedValue as VideoSource);
    },
    [options, onChange]
  );

  if (options.length === 0) return null;

  return (
    <ProviderPicker<VideoProviderMeta>
      options={options}
      value={value}
      onChange={handleChange}
      sortKeys={['price', 'quality', 'name']}
      defaultSortKey="price"
      disabled={disabled}
      groupBy={(opt) => opt.meta?.kind ?? null}
    />
  );
});
